/**
 * 健康心理条件の categoryTable（C×Lv）を一覧表示する。編集可／読み取り専用の両方に使う。
 */
import { Textarea } from '../ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

export const CATEGORY_TABLE_LEVELS = ['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5', 'Lv6'] as const;
export type CategoryTableLevel = typeof CATEGORY_TABLE_LEVELS[number];
export type CategoryTableData = Record<string, Partial<Record<CategoryTableLevel, string>>>;

/**
 * 旧ルーブリック（C1〜C16）の番号 → 名前。旧形式の条件表（行が C1〜C16）の行名にだけ使う。
 * 新しい CSV の行（SRC_Cn）は番号の意味が違う（旧C6=喫煙・新C6=マウスウォッシュ）ので、ここでは引かない。
 */
export const CATEGORY_LABELS: Record<string, string> = {
  C1: '主訴・現在直面している症状・問題',
  C2: '歯科受療行動・現在の状況',
  C3: 'セルフケア行動（ブラッシング・清掃用具）',
  C4: 'セルフケアの理解と阻害要因',
  C5: '飲食習慣・嗜好品摂取行動',
  C6: '喫煙・受動喫煙の状態',
  C7: '全身疾患・既往歴の把握',
  C9: '服薬状況と医師指示の理解',
  C10: '医科歯科連携と受診状況',
  C11: '生活リズム・睡眠・ストレス',
  C12: '生活環境・社会的背景',
  C13: '予防処置・専門的ケアの経験',
  C14: '要配慮者の口腔管理（子供、高齢者、障がい者等）',
  C15: '義歯・インプラント・補綴物の使用状況',
  C16: '口腔機能・嚥下・咀嚼',
};

export function categoryLabel(categoryId: string): string {
  // C1-T0 / C1-T1 のような形式にも対応: ベースID（例: C1）で引く
  const baseId = categoryId.split('-')[0];
  return CATEGORY_LABELS[baseId] ?? '';
}

interface ConditionCategoryTableProps {
  categoryTable?: CategoryTableData | null;
  editable?: boolean;
  onChangeCell?: (categoryId: string, level: CategoryTableLevel, value: string) => void;
  maxHeightClassName?: string;
}

function sortCategoryEntries([a]: [string, unknown], [b]: [string, unknown]) {
  // 新しい CSV の行は SRC_Cn。番号順に並べる
  const partsA = a.match(/^(?:SRC_)?C(\d+)(?:-(.+))?$/);
  const partsB = b.match(/^(?:SRC_)?C(\d+)(?:-(.+))?$/);
  const numA = partsA ? parseInt(partsA[1], 10) : 99;
  const numB = partsB ? parseInt(partsB[1], 10) : 99;
  if (numA !== numB) return numA - numB;
  return a.localeCompare(b);
}

export function ConditionCategoryTable({
  categoryTable,
  editable = false,
  onChangeCell,
  maxHeightClassName = 'max-h-[400px]',
}: ConditionCategoryTableProps) {
  const entries = Object.entries(categoryTable || {}).sort(sortCategoryEntries);
  if (entries.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <div className={`${maxHeightClassName} overflow-y-auto`}>
        <div className="min-w-max">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 top-0 z-20 min-w-[260px] bg-gray-100">カテゴリ</TableHead>
                {CATEGORY_TABLE_LEVELS.map((lv) => (
                  <TableHead key={lv} className="sticky top-0 z-10 min-w-[150px] bg-gray-100 text-xs">
                    {lv}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map(([categoryId, levels]) => {
                // 行のカテゴリ名（条件データ由来）を優先する。旧ルーブリックの名前は旧データ用の予備
                const rowName = (levels as Record<string, string | undefined> | undefined)?.category || categoryLabel(categoryId);
                return (
                <TableRow key={categoryId}>
                  <TableCell className="sticky left-0 z-10 min-w-[260px] bg-white text-xs">
                    <div className="font-mono font-semibold">{categoryId}</div>
                    {rowName && (
                      <div className="mt-0.5 text-[11px] font-normal text-gray-600 whitespace-normal break-words">
                        {rowName}
                      </div>
                    )}
                  </TableCell>
                  {CATEGORY_TABLE_LEVELS.map((lv) => {
                    const value = levels?.[lv] ?? '';
                    return (
                      <TableCell
                        key={lv}
                        className={`max-w-[220px] text-xs ${value ? '' : 'bg-gray-50 text-gray-300'}`}
                      >
                        {editable ? (
                          <Textarea
                            value={value}
                            onChange={(e) => onChangeCell?.(categoryId, lv, e.target.value)}
                            rows={4}
                            className="min-h-[96px] min-w-[180px] text-xs leading-5"
                            placeholder="-"
                          />
                        ) : (
                          <div className="min-h-[72px] whitespace-pre-wrap break-words py-1 text-gray-700">
                            {value || '-'}
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
